import { useRef, useState } from "react";

import {
  isSameDate,
  normalizeToDate,
  toBrowserLocalDay,
  toDate,
  toRelativeDate,
} from "common/utils/dates";
import { Feature, SubscriptionFrequency } from "common/types";
import { frequencyValueToString } from "common/utils/subscriptions";

import Alert from "client/common/components/content/Alert";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import Calendar from "client/common/components/calendars/Calendar";
import TextButton from "client/common/components/buttons/TextButton";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import { useFeatures } from "client/clinics/hooks";

import { useSubscriptionQuery, useUpdateSubscriptionMutation } from "../../hooks";
import { useSubscriptionScheduleConfirmDialog } from "../../stackables";
import {
  doesSubscriptionRequireAction,
  getRetentionOfferDeadlineWarning,
  getScheduleUpdate,
  minSelectableDeliveryDate,
  minSelectableNextOrderDate,
  projectUpcomingScheduleDates,
  shouldConfirmBumpedDate,
} from "../../utils";
import { DetailSubscription } from "../../types";
import SubscriptionFrequencySelect from "../SubscriptionFrequencySelect";
import SubscriptionDateBanners from "../SubscriptionDateBanners";
import SubscriptionAlert from "../SubscriptionAlert";

import styles from "../../styles/SubscriptionScheduleUpdateDrawer.module.less";

export type SubscriptionScheduleUpdateDrawerProps = {
  id: number;
};

type SubscriptionScheduleUpdateFormProps = {
  subscription: DetailSubscription;
};

const SubscriptionScheduleUpdateForm = ({ subscription }: SubscriptionScheduleUpdateFormProps) => {
  const { close } = useCurrentDrawer();
  const { open: openConfirmDialog } = useSubscriptionScheduleConfirmDialog();
  const isEdbEnabled = useFeatures()[Feature.AutoshipEDB];
  const {
    mutate: updateSubscription,
    isPending: isLoading,
    error,
  } = useUpdateSubscriptionMutation(subscription.id, {
    onSuccess: close,
  });

  const [frequency, setFrequency] = useState<SubscriptionFrequency>(
    subscription.is_active_frequency_valid
      ? subscription.active_frequency
      : subscription.recommended_frequency,
  );
  const [pickedDate, setPickedDate] = useState<Date>();
  const [calendarResetCount, setCalendarResetCount] = useState(0);
  const calendarRef = useRef<HTMLDivElement>(null);

  const currentDateSource = isEdbEnabled
    ? subscription.expected_delivery_by
    : subscription.next_order_date;
  const currentDate = currentDateSource ? normalizeToDate(currentDateSource) : undefined;

  const frequencyKey = frequencyValueToString(frequency);
  const isFrequencyChanged = frequencyKey !== frequencyValueToString(subscription.active_frequency);
  const selectedOption = [
    subscription.active_frequency,
    subscription.recommended_frequency,
    ...subscription.alternative_frequencies,
  ].find((option) => frequencyValueToString(option) === frequencyKey);
  const optionDates = (
    (isEdbEnabled ? selectedOption?.expected_delivery_dates : selectedOption?.next_order_dates) ??
    []
  ).map((date) => normalizeToDate(date));

  const dateLabel = isEdbEnabled ? "Next estimated delivery" : "Next order date";
  const earliestDeliveryDate = minSelectableDeliveryDate();
  // A current delivery date sooner than the earliest selectable one starts on the earliest, so the
  // client can save it without picking a day.
  const isCurrentDateTooSoon =
    isEdbEnabled &&
    !isFrequencyChanged &&
    !!optionDates[0] &&
    optionDates[0] < earliestDeliveryDate;
  const suggestedDate = isCurrentDateTooSoon ? earliestDeliveryDate : optionDates[0];
  const dateToSave = pickedDate ?? (isCurrentDateTooSoon ? earliestDeliveryDate : undefined);
  const selectedDate = pickedDate ?? suggestedDate;
  const selectedCalendarDay = selectedDate && toBrowserLocalDay(selectedDate);
  const upcomingDates = dateToSave
    ? projectUpcomingScheduleDates(dateToSave, frequency)
    : optionDates;
  const isDateChanged = !!dateToSave && !isSameDate(dateToSave, currentDate);
  const needsBumpConfirm = shouldConfirmBumpedDate({
    isFrequencyChanged,
    pickedDate: dateToSave,
    dateWasBumped: !!selectedOption?.date_was_bumped,
  });

  const retentionOfferDeadlineWarning = getRetentionOfferDeadlineWarning({
    offer: subscription.accepted_retention_offer,
    scheduleDate: selectedDate,
    isEdbEnabled,
  });

  const save = () => {
    updateSubscription(
      getScheduleUpdate({ frequency, isFrequencyChanged, pickedDate: dateToSave, isEdbEnabled }),
    );
  };

  const submit = () => {
    if (needsBumpConfirm && selectedDate) {
      openConfirmDialog({ bumpedDate: selectedDate, onConfirm: save });
      return;
    }
    save();
  };

  const resetToSuggestedDate = () => {
    setPickedDate(undefined);
    setCalendarResetCount((count) => count + 1);
    // The button unmounts on click, so focus moves back to the calendar instead of the page body.
    calendarRef.current?.focus();
  };

  const getDescription = () => {
    const frequencyDescription = `Currently ships ${subscription.active_frequency.display.toLowerCase()}.`;
    if (!currentDate) {
      return frequencyDescription;
    }
    const dateDescription = isEdbEnabled
      ? `Your next estimated delivery is ${toDate(currentDate)}`
      : `Your next order will be placed on ${toDate(currentDate)}`;
    return `${frequencyDescription} ${dateDescription} (${toRelativeDate(currentDate)}).`;
  };

  return (
    <DrawerLayout>
      <DrawerLayout.Header title="Update schedule" description={getDescription()} onClose={close} />
      <DrawerLayout.Form
        size="l"
        submitText="Save changes"
        canSubmit={
          (isFrequencyChanged || isDateChanged) && !doesSubscriptionRequireAction(subscription)
        }
        onSubmit={submit}
        isLoading={isLoading}
        error={error}
        fieldErrors={[
          error?.errors.action,
          error?.errors.frequency,
          error?.errors.interval_unit,
          isEdbEnabled ? error?.errors.expected_delivery_by : error?.errors.next_order_date,
        ]}
      >
        {doesSubscriptionRequireAction(subscription) && (
          <DrawerLayout.Alerts>
            <SubscriptionAlert subscription={subscription} />
          </DrawerLayout.Alerts>
        )}
        <DrawerLayout.Form.Group>
          <DrawerLayout.Form.Label>Frequency</DrawerLayout.Form.Label>
          <SubscriptionFrequencySelect
            subscription={subscription}
            value={frequency}
            onSelect={setFrequency}
          />
        </DrawerLayout.Form.Group>
        <DrawerLayout.Form.Group>
          <DrawerLayout.Form.Label>{dateLabel}</DrawerLayout.Form.Label>
          {isEdbEnabled && (
            <div className={styles.disclaimer}>
              Earliest available delivery: {toDate(earliestDeliveryDate)}
            </div>
          )}
          <div ref={calendarRef} tabIndex={-1} role="group" aria-label={dateLabel}>
            <Calendar
              // The calendar only follows `month` across months, so a new frequency or a reset
              // remounts it to show the date even after the client has browsed elsewhere.
              key={`${frequencyKey}-${calendarResetCount}`}
              disabledDays={{
                before: toBrowserLocalDay(
                  isEdbEnabled ? earliestDeliveryDate : minSelectableNextOrderDate(),
                ),
              }}
              selectedDays={selectedCalendarDay}
              month={selectedCalendarDay}
              modifiers={{ previous: currentDate && toBrowserLocalDay(currentDate) }}
              // Clicking the suggested date isn't a pick, so a bumped date still gets its confirm.
              onSelect={(day) => setPickedDate(isSameDate(day, suggestedDate) ? undefined : day)}
            />
          </div>
          {pickedDate && optionDates.length > 0 && (
            <TextButton hasUnderline={true} onClick={resetToSuggestedDate}>
              Use suggested date
            </TextButton>
          )}
        </DrawerLayout.Form.Group>
        {upcomingDates.length > 0 && (
          <DrawerLayout.Form.Group>
            <DrawerLayout.Form.Label>
              {isEdbEnabled
                ? "Your next estimated delivery dates will be:"
                : "Your next order dates will be:"}
            </DrawerLayout.Form.Label>
            <SubscriptionDateBanners dates={upcomingDates} />
            {retentionOfferDeadlineWarning && (
              <Alert variant="warning">{retentionOfferDeadlineWarning}</Alert>
            )}
          </DrawerLayout.Form.Group>
        )}
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

const SubscriptionScheduleUpdateDrawer = ({ id }: SubscriptionScheduleUpdateDrawerProps) => {
  const { data: subscription, isLoading, error } = useSubscriptionQuery(id);

  if (isLoading) {
    return <DrawerLayout.Loading />;
  }

  if (!subscription || error) {
    return <DrawerLayout.FetchError entityName="Autoship" error={error} />;
  }

  return <SubscriptionScheduleUpdateForm subscription={subscription} />;
};

export default SubscriptionScheduleUpdateDrawer;
